// FILE: components/explore/ExploreHeader.tsx
"use client";

import { useEffect, useRef, useState } from "react";
import { Search, BookOpen, FileText, MessageSquare, SlidersHorizontal, Sparkles } from "lucide-react";
import type { SearchTab, SortOption } from "./_shared/types";

interface Props {
  keyword: string;
  onKeywordChange: (v: string) => void;
  activeTab: SearchTab;
  onTabChange: (t: SearchTab) => void;
  sort: SortOption;
  onSortChange: (s: SortOption) => void;
  onOpenMobileFilter: () => void;
  resultCount?: number;
}

const TABS: { id: SearchTab; label: string; icon: typeof Search }[] = [
  { id: "all", label: "Tất cả", icon: Sparkles },
  { id: "paths", label: "Lộ trình", icon: BookOpen },
  { id: "exams", label: "Đề thi", icon: FileText },
  { id: "articles", label: "Reviews", icon: MessageSquare },
];

const SORTS: { value: SortOption; label: string }[] = [
  { value: "hot", label: "🔥 Phổ biến" },
  { value: "newest", label: "🕐 Mới nhất" },
  { value: "rating", label: "⭐ Rating cao" },
  { value: "free", label: "🆓 Miễn phí" },
];

export default function ExploreHeader({
  keyword,
  onKeywordChange,
  activeTab,
  onTabChange,
  sort,
  onSortChange,
  onOpenMobileFilter,
  resultCount,
}: Props) {
  const [condensed, setCondensed] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Intersection Observer: detect khi hero scroll ra khỏi viewport → bật condensed
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => setCondensed(!entry.isIntersecting),
      { rootMargin: "-1px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* ═══ HERO (expanded) ═══ */}
      <div className="bg-gradient-to-br from-teal-50 via-white to-orange-50 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
            Khám phá JB AI
          </h1>
          <p className="text-sm text-slate-600 mb-5">
            Hàng nghìn lộ trình, đề thi và reviews từ cộng đồng
          </p>

          {/* Search bar to */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => onKeywordChange(e.target.value)}
              placeholder="Tìm lộ trình, đề thi, review..."
              className="w-full pl-12 pr-4 py-3.5 bg-white border border-slate-200 rounded-2xl text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>
      </div>

      {/* Sentinel: khi sentinel này scroll ra khỏi viewport → condensed = true */}
      <div ref={sentinelRef} className="h-px" />

      {/* ═══ STICKY BAR (tabs + sort + condensed search) ═══ */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 py-3">
            {/* Condensed search (chỉ hiện khi scroll xuống) */}
            {condensed && (
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => onKeywordChange(e.target.value)}
                  placeholder="Tìm kiếm..."
                  className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-full text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white"
                />
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 flex-1 overflow-x-auto scrollbar-hide">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => onTabChange(id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                    activeTab === id
                      ? "bg-teal-800 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {/* Sort dropdown */}
            <div className="relative shrink-0">
              <select
                value={sort}
                onChange={(e) => onSortChange(e.target.value as SortOption)}
                className="appearance-none pl-3 pr-8 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-semibold text-slate-700 hover:bg-slate-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Mobile filter button */}
            <button
              onClick={onOpenMobileFilter}
              className="lg:hidden shrink-0 p-2 bg-slate-50 border border-slate-200 rounded-full hover:bg-slate-100"
            >
              <SlidersHorizontal className="w-4 h-4 text-slate-700" />
            </button>
          </div>

          {/* Result count (optional) */}
          {typeof resultCount === "number" && (
            <div className="pb-2 text-xs text-slate-500">
              {resultCount} kết quả
            </div>
          )}
        </div>
      </div>
    </>
  );
}